import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { FeatureName, useIsFeatureEnabled } from '@gripp/shared-logic';
import {
  Colors,
  ColumnData,
  EntityAlertListModal,
  GroupsList,
  ListCountProvider,
  PanelShell,
  SelectedGroupRow,
  SelectionStatus,
} from '@gripp/shared-ui';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleProp, StyleSheet, Text, View } from 'react-native';
import { ModalButton } from '../../shared/components/modalButton';
import { AssignmentType, GroupAssignmentForm } from './groupAssignmentForm';
import { Groups as GroupsOld } from './groups-old';
import { GroupNewPanel } from './groupNewPanel';
import { GroupsCreationProvider } from './groupsCreationContext';
import { GroupsFilters } from './groupsFilters';
import {
  GroupSelectionPanelHeader,
  GroupsSelectionPanelMain,
} from './groupsSelectionPanel';

export const Groups = () => {
  const isCropCircleEnable = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  if (isCropCircleEnable) {
    return <GroupsBase />;
  }

  return <GroupsOld />;
};

export const GroupsBase = ({
  rightColumn: rightColumnOverride,
}: {
  rightColumn?: ColumnData;
} = {}) => {
  const { t, i18n } = useTranslation();
  const [showAlertListModal, setShowAlertListModal] = useState(false);
  const [selectedGroupIdForAlertList, setSelectedGroupIdForAlertList] =
    useState<string | undefined>(undefined);

  const [rowsSelected, setRowsSelected] = useState<SelectedGroupRow[]>([]);
  const [selectionStatus, setSelectionStatus] =
    useState<SelectionStatus>('idle');
  const [isCreating, setIsCreating] = useState(false);

  const rowsSelectedIds = useMemo(
    () => rowsSelected.map((r) => r.id),
    [rowsSelected]
  );
  const hasSelection = rowsSelected.length > 0;

  const clearSelection = useCallback(() => {
    setRowsSelected([]);
    setSelectionStatus('reset');
    rightColumnOverride?.onBackButtonPress?.();
  }, [rightColumnOverride]);

  const openNew = useCallback(() => {
    setRowsSelected([]);
    setSelectionStatus('reset');
    setIsCreating(true);
  }, []);

  const closeNew = useCallback(() => {
    setIsCreating(false);
  }, []);

  const handleRowsSelectedChange = useCallback(
    (rows: SelectedGroupRow[]) => {
      setRowsSelected(rows);
      if (rows.length > 0 && isCreating) setIsCreating(false);
    },
    [isCreating]
  );

  const creationContextValue = useMemo(
    () => ({ isCreating, openNew, closeNew }),
    [isCreating, openNew, closeNew]
  );

  const getLabel = (labelStyle: StyleProp<any>, label: string) => {
    return <Text style={[labelStyle, styles.cellText]}>{label}</Text>;
  };

  const AssetsButton = (data: any) => {
    return (
      <ModalButton
        label=""
        customButton={
          <View style={[styles.iconContainer]}>
            <MaterialCommunityIcons
              name="qrcode"
              color={Colors.blackText}
              size={16}
              style={styles.icon}
            />
            {getLabel(styles.countText, data.assets)}
          </View>
        }
        modalComponent={({ onComplete }) => {
          return (
            <GroupAssignmentForm
              groupId={data.id}
              title={data.name}
              onComplete={onComplete}
              assignmentType={AssignmentType.Assets}
            />
          );
        }}
        containerStyle={styles.modalContainer}
      />
    );
  };

  const PeopleButton = (data: any) => {
    return (
      <ModalButton
        label=""
        customButton={
          <View style={[styles.iconContainer]}>
            <MaterialCommunityIcons
              name="account-multiple"
              color={Colors.blackText}
              size={16}
              style={styles.icon}
            />
            {getLabel(styles.countText, data.people)}
          </View>
        }
        modalComponent={({ onComplete }) => {
          return (
            <GroupAssignmentForm
              groupId={data.id}
              title={data.name}
              onComplete={onComplete}
              assignmentType={AssignmentType.People}
            />
          );
        }}
        containerStyle={styles.modalContainer}
      />
    );
  };

  const RoutinesButton = (data: any) => {
    return (
      <ModalButton
        label=""
        customButton={
          <View style={[styles.iconContainer]}>
            <MaterialCommunityIcons
              name="format-list-checks"
              color={Colors.blue}
              size={16}
              style={styles.icon}
            />
            {getLabel(styles.countText, data.routines)}
          </View>
        }
        modalComponent={({ onComplete }) => {
          return (
            <GroupAssignmentForm
              groupId={data.id}
              title={data.name}
              onComplete={onComplete}
              assignmentType={AssignmentType.Routines}
            />
          );
        }}
        containerStyle={styles.modalContainer}
      />
    );
  };

  const GroupAlertsButton = (data: any) => {
    return (
      <Pressable
        onPress={() => {
          setSelectedGroupIdForAlertList(data.id);
          setShowAlertListModal(true);
        }}
        style={[styles.iconContainer]}
      >
        <MaterialCommunityIcons
          name="alarm-check"
          color={Colors.blackText}
          size={16}
          style={styles.icon}
        />
        {getLabel(styles.countText, data.groupAlerts)}
      </Pressable>
    );
  };

  const filterComponent = <GroupsFilters />;
  const [count, setCount] = useState<number>();

  const columnComponent = (
    <View style={styles.groupsContainer}>
      <GroupsList
        assetButton={AssetsButton}
        peopleButton={PeopleButton}
        routinesButton={RoutinesButton}
        groupAlertsButton={GroupAlertsButton}
        onRowsSelectedChange={handleRowsSelectedChange}
        selectedRowIds={rowsSelectedIds}
        selectionStatus={selectionStatus}
        onSelectionStatusChange={setSelectionStatus}
        onItemsCountChange={setCount}
      />
      {showAlertListModal && selectedGroupIdForAlertList && (
        <EntityAlertListModal
          visible={showAlertListModal}
          entityType="group"
          entityId={selectedGroupIdForAlertList}
          onClose={() => {
            setShowAlertListModal(false);
            setSelectedGroupIdForAlertList(undefined);
          }}
        />
      )}
    </View>
  );

  const newGroupRightColumn = useMemo<ColumnData | undefined>(() => {
    if (!isCreating) return undefined;
    return {
      title: 'groups.create.newGroup',
      mainComponent: <GroupNewPanel onComplete={closeNew} />,
      showBackButton: true,
      onBackButtonPress: closeNew,
      disableScroll: true,
    };
  }, [isCreating, closeNew]);

  const selectionRightColumn = useMemo<ColumnData | undefined>(() => {
    if (!hasSelection) return undefined;

    const count = rowsSelected.length;
    const title =
      count === 1
        ? t('groups.topBar.groupSelected', {
            defaultValue: '1 group selected',
          })
        : t('groups.topBar.groupsSelected', {
            count,
            defaultValue: `${count} groups selected`,
          });
    i18n.addResource(i18n.language, 'translation', title, title);

    // Show the Create Alert header button only when a single group is selected
    // (alerts are scoped to one group at a time).
    const singleGroupId =
      rowsSelectedIds.length === 1 ? rowsSelectedIds[0] : undefined;

    return {
      title,
      mainComponent: (
        <GroupsSelectionPanelMain
          groupIds={rowsSelectedIds}
          onComplete={clearSelection}
        />
      ),
      headerComponent: singleGroupId ? (
        <GroupSelectionPanelHeader groupId={singleGroupId} />
      ) : undefined,
      showBackButton: true,
      onBackButtonPress: clearSelection,
      disableScroll: true,
    };
  }, [hasSelection, rowsSelected, rowsSelectedIds, clearSelection, t, i18n]);

  return (
    <GroupsCreationProvider value={creationContextValue}>
      <ListCountProvider count={count}>
        <PanelShell
          leftColumn={{
            title: 'groups.menu.title',
            count,
            mainComponent: columnComponent,
            headerComponent: filterComponent,
            disableScroll: true,
          }}
          rightColumnOverride={
            newGroupRightColumn ?? selectionRightColumn ?? rightColumnOverride
          }
        />
      </ListCountProvider>
    </GroupsCreationProvider>
  );
};

const styles = StyleSheet.create({
  filtersContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  groupSearchContainer: {
    width: 390,
    marginRight: 20,
  },
  groupsContainer: {
    flex: 1,
    overflow: 'hidden',
    zIndex: -1,
  },
  modalContainer: {
    width: 640,
    height: '80%',
    minHeight: 450,
    alignSelf: 'center',
    backgroundColor: Colors.primaryGrayBackground,
    borderRadius: 10,
  },
  iconContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  icon: {
    marginRight: 2,
  },
  countText: {
    fontSize: 16,
  },
  cellText: {
    fontSize: 16,
    fontWeight: '500',
    flexWrap: 'nowrap',
  },
  headerButtonsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
});
